import React, { useState, useEffect, memo, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';
import ListSubheader from '@material-ui/core/ListSubheader';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import Badge from '@material-ui/core/Badge';

import Skeleton from '@material-ui/lab/Skeleton';

import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: theme.spacing((props.level * 2)),
    }),
    listItemText: props => ({
        marginTop: '0px',
        marginBottom: '0px',
        paddingTop: '4px',
        paddingBottom: '4px',
        color: (props.disabled ? theme.palette.text.disabled : theme.palette.text.primary )
    }),
    listItemTextSecondary: props => ({
        color: (props.disabled ? theme.palette.text.disabled : theme.palette.text.secondary )
    }),
    badge: {
        paddingLeft: '35px'
    },
}));

const useListStyles = makeStyles(theme => ({
    root: {
        height: '100%'
    },
    subHeader: {
        backgroundColor: theme.palette.grey[200],
        zIndex: '10'
    },
    empty: {
        position: 'absolute',
        top: '0px',
        bottom: '0px',
        left: '0px',
        right: '0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

function NestedListItem(props) {

    const { group, level, selectedId, unselectableId, onSelect, enableEdit, onEdit, variant, openParent } = props;

    const styleProp = {
        level: level,
        disabled: (unselectableId == group.id) ? true : false
    }
    const classes = useStyles(styleProp);

    const [ open, setOpen ] = React.useState(false);

    function toggleOpen() {
        setOpen(!open)
    }

    function isSelected(id) {
        return (id == selectedId) ;
    }

    function openChain() {
        setOpen(true);
        openParent();
    }

    useEffect(() => {
        if (group.id == selectedId) {
            openChain();
        }
    }, []);

    if (group.children_with_count.length > 0) {
        return (
            <Fragment>
                <ListItem
                    classes={{ root: classes.nested }}
                    button
                    selected={isSelected(group.id)}
                    >
                    <ListItemIcon classes={{ root:classes.listItemIcon }}>
                        <IconButton edge="start" aria-label="open" onClick={toggleOpen} size="medium">
                            {open ? <ExpandLess fontSize="inherit"/> : <ExpandMore fontSize="inherit"/>}
                        </IconButton>
                    </ListItemIcon>
                    <ListItemText
                        primary={(
                            <span>
                                <span>{group.title}</span>
                                <Badge
                                    classes={{ root: classes.badge }}
                                    color={group.public ? 'primary' : 'error'}
                                    badgeContent={group.public ? 'Public' : 'Private'}
                                />
                                <span className={classes.badge}>ID: {group.id}</span>
                            </span>
                        )}
                        secondary={(
                            <span>
                                <span>({group.total_user_count}) Total </span>
                                <span>({group.user_count}) in Group</span>
                            </span>
                        )}
                        onClick={(unselectableId == group.id) ? null : (e) => onSelect(group.id)}
                        classes={{ root: classes.listItemText, secondary: classes.listItemTextSecondary }}
                    />
                    {enableEdit &&
                        <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="edit" onClick={(e) => onEdit(group.id)} size="medium">
                                <EditIcon fontSize="inherit"/>
                            </IconButton>
                        </ListItemSecondaryAction>
                    }
                </ListItem>
                <Collapse in={open} timeout="auto">
                    <List dense component="div" disablePadding>
                        {group.children_with_count.map((group) => (
                            <NestedListItem
                                key={group.id}
                                group={group}
                                level={(level + 1)}
                                selectedId={selectedId}
                                unselectableId={unselectableId}
                                onSelect={onSelect}
                                enableEdit={enableEdit}
                                onEdit={onEdit}
                                variant={variant}
                                openParent={openChain}
                                />
                        ))}
                    </List>
                </Collapse>
            </Fragment>
        );
    } else {
        return (
            <ListItem
                classes={{ root: classes.nested }}
                button
                selected={isSelected(group.id)}
                disabled={(unselectableId == group.id) ? true : false}
                >
                <ListItemText
                    primary={(
                        <span>
                            <span>{group.title}</span>
                            <Badge
                                classes={{ root: classes.badge }}
                                color={group.public ? 'primary' : 'error'}
                                badgeContent={group.public ? 'Public' : 'Private'}
                            />
                            <span className={classes.badge}>ID: {group.id}</span>
                        </span>
                    )}
                    secondary={'(' + group.user_count + ') User' + ((group.user_count == 1) ? '' : 's') }
                    onClick={(e) => onSelect(group.id)}
                    classes={{ root: classes.listItemText, secondary: classes.listItemTextSecondary }}
                    inset={(variant == 'level')}
                />
                {enableEdit &&
                    <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="edit" onClick={(e) => onEdit(group.id)} size="medium">
                            <EditIcon fontSize="inherit"/>
                        </IconButton>
                    </ListItemSecondaryAction>
                }
            </ListItem>
        )
    }
}

export default function GroupSelect(props) {

    const classes = useListStyles();
    const api = useApi();

    const [ loading, setLoading ] = useState(false);
    const [ groupOptions, setGroupOptions ] = useState([]);
    const [ selectedId, setSelectedId ] = useState(0)
    const {
        initGroupId,
        initGroupOptions,
        onSelect,
        unselectableId,
        variant,
        label,
        enableEdit,
        onEdit,
        groupRefresh,
        firstParentId,
        optionsLoading
    } = props;

    useEffect(() => {
        setSelectedId((initGroupId || 0));
    }, [initGroupId]);

    const getGroupOptions = (group_id = 0) => {
        setLoading(true);

        api.get('/admin/group/' + variant + '/recursive/' + group_id)
            .then(res => {
                setGroupOptions(res.groups);
            }).catch( error => {
                console.log(error);
            }).then(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (initGroupOptions) {
            setGroupOptions(initGroupOptions)
        } else if (firstParentId) {
            getGroupOptions(firstParentId)
        } else {
            getGroupOptions();
        }
    }, [initGroupOptions, groupRefresh, firstParentId]);

    useEffect(() => {
        if (optionsLoading) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [optionsLoading])

    const handleSelect = id => {
        if (id !== unselectableId) {
            if (id == selectedId) {
                setSelectedId(0);
                onSelect(0);
            } else {
                setSelectedId(id)
                onSelect(id);
            }
        }
    }

    return (
        <List
            dense
            aria-labelledby="group-level-label"
            subheader={
                label && (
                    <ListSubheader className={classes.subHeader} component="div" id="group-level-label">
                        {label}
                    </ListSubheader>
                )
            }
            className={classes.root}
            >
            {loading ? (
                <Fragment>
                    <ListItem>
                        <ListItemText disableTypography
                            primary={<Skeleton variant="text" height={6} width="40%"/>}
                            secondary={<Skeleton variant="text" height={6} width="80%"/>}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText disableTypography
                            primary={<Skeleton variant="text" height={6} width="50%"/>}
                            secondary={<Skeleton variant="text" height={6} width="40%"/>}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText disableTypography
                            primary={<Skeleton variant="text" height={6} width="20%"/>}
                            secondary={<Skeleton variant="text" height={6} width="70%"/>}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText disableTypography
                            primary={<Skeleton variant="text" height={6} width="60%"/>}
                            secondary={<Skeleton variant="text" height={6} width="50%"/>}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText disableTypography
                            primary={<Skeleton variant="text" height={6} width="30%"/>}
                            secondary={<Skeleton variant="text" height={6} width="50%"/>}
                        />
                    </ListItem>
                </Fragment>

            ) : groupOptions.length ?
                groupOptions.map((group) => (
                    <NestedListItem
                        key={group.id}
                        group={group}
                        selectedId={selectedId}
                        level={2}
                        unselectableId={unselectableId}
                        onSelect={handleSelect}
                        enableEdit={enableEdit}
                        onEdit={onEdit}
                        variant={variant}
                        openParent={() => {}}
                        />
                ))
            : (
                <Typography color="error" className={classes.empty} variant="subtitle2">
                    You have no {variant}s available.
                </Typography>
            )}
        </List>
    )
}
